import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { formatNumber } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState } from "../../../utils/GridUtils";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import IconButton from "@mui/material/IconButton";
import { Button, Stack, Tooltip, useTheme } from "@mui/material";
import { AddRounded, DeleteForeverRounded, EditAttributesRounded, InfoRounded, ModeRounded } from "@mui/icons-material";
import { STATE_VOICES_ACTIVE_FILTERS, STATE_VOICES_TABLE_UPDATING } from "../constants/ClustersRecoil";
import FinanceDialog from "../../../component/FinanceDialog";
import { useClusterVoicesRepository } from "../../../api/repositories/ClusterVoicesRepository";
import VoiceModal from "../modals/VoiceModal";
import { useParams } from "react-router-dom";
import EmptyState from "../../../../../util/component/EmptyState";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { useHasScope } from "../../../../../config/globalConfig";
import { ADD_VOICE_TO_CLUSTER, DELETE_VOICE_CLUSTER, EDIT_VOICE_CLUSTER } from "../../../utils/FinanceScope";

const VoicesTable = ({legalEntity, dealer, type}: {legalEntity: string, dealer: string, type: string}) => {

    const UNIQUE_ID = "fnn-clusters-voices";

    const {t} = useTranslation("finance", {keyPrefix: "clusters"});
    const {getVoices, deleteVoice} = useClusterVoicesRepository();
    const {id} = useParams();
    const hasScope = useHasScope;
    const filterDefs = useRecoilValue(STATE_VOICES_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_VOICES_TABLE_UPDATING);

    const gridRef = useRef<AgGridReact>(null);
    const theme = useTheme();

    const [openModal, setOpenModal] = useState(false);
    const [modalType, setModalType] = useState('add');
    const [voice, setVoice] = useState();
    const [warningOpen, setWarningOpen] = useState(false);
    const [voiceToDelete, setVoiceToDelete] = useState<any>({});

    const columnDefs : ColDef[] = [
        {
            field: "id", headerName: t("tables.voice.id"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            pinned: "left", lockPosition: "left", lockVisible: true, lockPinned: true,
        },
        {
            field: "appliedOn", headerName: t("tables.voice.applied-on"),
            minWidth: 150,
            unSortIcon: true, sortable: true,
            valueFormatter: (params: any) => t(`applied-on.${params.value}`)
        },
        {
            field: "printoutType", headerName: t("tables.voice.printout-type"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            valueFormatter: (params: any) => t(`printout-types.${params.value}`)
        },
        {
            field: "special", headerName: t("tables.voice.special"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            valueFormatter: (params: any) => !!params.value ? t(`specials.${params.value}`) : ''

        },
        {
            field: "ord", headerName: t("tables.voice.order"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => formatNumber(params.value),
        },
        {
            field: "voice", headerName: t("tables.voice.voice-code"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field:"voiceName", headerName: t("tables.voice.voice-name"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field:"segment", headerName: t("tables.voice.segment"),
            minWidth: 200,
            valueFormatter: (params: any) => params?.value ? t(`segments.${params.value}`) : "",
            unSortIcon: true, sortable: true,
        },
        {
            field: "dd1To7", headerName: t("tables.voice.dd-1_7"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "dd8To14", headerName: t("tables.voice.dd-8_14"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "dd15To21", headerName: t("tables.voice.dd-15_21"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "dd22To31", headerName: t("tables.voice.dd-22_31"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
        },
        {
            field: "fuelOnly", headerName: t("tables.voice.fuel-only"),
            minWidth: 200,
            unSortIcon: true, sortable: true,
            cellRenderer: (params: any) => {
                if (params.value === true) return <EditAttributesRounded color={'primary'} sx={{marginLeft: '5px'}}/>
            }
        },
        {
            field: "_actions", headerName: '',
            minWidth: 100, maxWidth: 110,
            unSortIcon: false, sortable: false,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => (
                        <FinanceDynamicActions>
                            {hasScope(EDIT_VOICE_CLUSTER) && 
                            <Tooltip arrow title={t('tables.voice.actions.tooltips.edit')}>
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    onClick={() => {
                                        setVoice(params.data);
                                        setModalType('edit');
                                        setOpenModal(true);
                                    }}
                                >
                                    <ModeRounded/>
                                </IconButton>
                            </Tooltip>
                            }
                            {hasScope(DELETE_VOICE_CLUSTER) &&
                            <Tooltip arrow title={t('tables.voice.actions.tooltips.delete')}>
                                <IconButton
                                    sx={{color: theme.palette.accent.main}}
                                    onClick={() => {
                                        setVoiceToDelete({id: params.data.id, voice: params.data.voice});
                                        setWarningOpen(true);
                                    }}
                                >
                                    <DeleteForeverRounded/>
                                </IconButton>
                            </Tooltip>
                            }
                        </FinanceDynamicActions>
                    ),
                };
            },
            suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {title: t('tables.voice.no-rows-overlay.title')}
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
            setUpdating(false);
        }
    }, [updating]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort;

                getVoices(id!, startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;

                        if (result.page.totalElements === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result.page.totalElements,
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api?.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
            <></>
        ),
        renderActions: () => (
            <Stack>
                {hasScope(ADD_VOICE_TO_CLUSTER) &&
                <Button fullWidth color={"accent"} variant="contained" component="label"
                        startIcon={<AddRounded/>}
                        onClick={() => {
                            setModalType('add');
                            setOpenModal(true);
                        }}>
                    {t('preview.buttons.add-voice')}
                </Button>
                }
            </Stack>
        )
    }

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                noRowsOverlayComponent={noRowsOverlayComponent}
                rowModelType="serverSide"
                pagination paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowSelection={"multiple"}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(filterDefs, gridRef.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    setLocalGridState(UNIQUE_ID, {
                        rows: gridRef.current!.api?.paginationGetPageSize(),
                        cols: getLocalGridState(UNIQUE_ID).cols
                    });
                }}
            ></AgGridCustomTable>
            <FinanceDialog open={warningOpen}
                           onClose={() => setWarningOpen(false)}
                           callback={() => {
                               deleteVoice(voiceToDelete.id!).then(() => setUpdating(true))
                           }}
                           titleIcon={<InfoRounded style={{color: 'accent.main'}}/>}
                           title={t('voice-delete-confirmation.title')}
                           content={t('voice-delete-confirmation.text', {voice: voiceToDelete?.voice})}
                           confirmButton={t('voice-delete-confirmation.confirm')}
                           cancelButton={t('voice-delete-confirmation.cancel')}
            />
            <VoiceModal open={openModal}
                        onClose={() => setOpenModal(false) }
                        type={modalType} voice={voice}
                        clusterId={parseInt(id!)}
                        legalEntity={legalEntity} dealer={dealer}
                        clusterType={type}
            />
        </>
    );
};

export default VoicesTable;
